import { useEffect } from 'react';
import { useMultiHOA } from '../MultiHOAContext';
import { api } from '../api';

/**
 * Component that initializes multi-HOA context when user logs in or refreshes
 * Must be placed inside both AuthProvider and MultiHOAProvider
 */
export const MultiHOAInitializer = ({ user }) => {
  const { activeHoaId, setActiveHoaId, setUserHoas, userHoas } = useMultiHOA();

  useEffect(() => {
    const initializeMultiHOA = async () => {
      if (!user) {
        // User logged out - clear context
        setActiveHoaId(null);
        setUserHoas([]);
        return;
      }

      try {
        console.log('🏘️ Initializing multi-HOA context for user:', user.id);
        
        // Fetch all HOAs the user belongs to
        const response = await api.get('/api/user-context/hoas');
        const hoas = response.data || [];
        
        console.log('🏘️ User belongs to', hoas.length, 'HOA(s):', hoas);
        setUserHoas(hoas);

        // Type-safe equality helper (handles string vs number comparison)
        const eq = (a, b) => String(a) === String(b);

        // Determine active HOA
        let hoaToActivate = null;

        // 1. Check if there's already an activeHoaId in localStorage
        const storedActiveHoaId = localStorage.getItem('active_hoa_id');
        console.log('🏘️ [INIT] Checking localStorage for active_hoa_id:', storedActiveHoaId);
        console.log('🏘️ [INIT] Current activeHoaId in state:', activeHoaId);
        console.log('🏘️ [INIT] User primary HOA:', user.hoa_id);
        console.log('🏘️ [INIT] Available HOAs:', hoas.map(h => `${h.hoa_name} (${h.hoa_id})`).join(', '));
        
        if (storedActiveHoaId && hoas.some(h => eq(h.hoa_id, storedActiveHoaId))) {
          hoaToActivate = storedActiveHoaId;
          console.log('🏘️ [INIT] ✅ Using stored active HOA from localStorage:', hoaToActivate);
        }
        // 2. Fall back to user's primary HOA
        else if (user.hoa_id && hoas.some(h => eq(h.hoa_id, user.hoa_id))) {
          hoaToActivate = String(user.hoa_id);
          console.log('🏘️ [INIT] ⚠️ No valid stored HOA, using user primary HOA:', hoaToActivate);
        }
        // 3. Fall back to first HOA in list
        else if (hoas.length > 0) {
          hoaToActivate = hoas[0].hoa_id;
          console.log('🏘️ [INIT] ⚠️ Using first available HOA as fallback:', hoaToActivate);
        }

        if (hoaToActivate && hoaToActivate !== activeHoaId) {
          console.log('🏘️ [INIT] Setting active HOA to:', hoaToActivate, '(was:', activeHoaId, ')');
          setActiveHoaId(hoaToActivate);
        } else {
          console.log('🏘️ [INIT] Active HOA already correct:', activeHoaId);
        }
      } catch (error) {
        console.error('❌ Failed to initialize multi-HOA context:', error);
        // Fallback to user's primary HOA
        if (user.hoa_id) {
          console.log('🏘️ Falling back to user primary HOA:', user.hoa_id);
          setActiveHoaId(user.hoa_id);
          setUserHoas([{
            hoa_id: user.hoa_id,
            hoa_name: 'My HOA',
            is_primary: true
          }]);
        }
      }
    };

    initializeMultiHOA();
  }, [user?.id]); // Re-run when user changes (login/logout)

  return null; // This component doesn't render anything
};

export default MultiHOAInitializer;
